import React, { FunctionComponent, useState, useCallback, useEffect } from 'react';
import { Presentation } from './presentation';
import { EventContentsSectionProps as Props, EventContentsSectionState, ModalMode } from './types';
import { applyEventContent, cancelEventContent, EntryEventContentApiError } from '../../../services/entryEventContent';
import { useModal } from '../../../hooks/useModal';
import { useLoginRegisterModals } from '../../../hooks/useLoginRegisterModals';
import { getCsrfToken } from '../../../libs/csrfToken';
import { useDevice } from '../../../hooks/device';

// 主催企業の社員は申し込めない(サーバー側 entry_event_contents#create の文言と合わせる)
const COMPANY_PERSON_MESSAGE = '社員は応募できません';

export const Component: FunctionComponent<Props> = (props) => {
  const { isPc } = useDevice();
  const [eventContents, setEventContents] = useState(props.eventContents || []);
  const [state, setState] = useState<EventContentsSectionState>({
    selectedEventContent: null,
    isProcessing: false,
  });

  const [modalMode, setModalMode] = useState<ModalMode>('confirm');
  const { isOpen: isEntryModalOpen, open: openEntryModal, close: closeEntryModal } = useModal(false, { lockBody: false });

  const {
    isLoginModalOpen,
    isRegisterModalOpen,
    handleToggleLoginModal,
    handleToggleRegisterModal,
    handleChangeModal,
    redirectUrl,
    isRecruitmentOrEventOrTvOrCompanyPath,
  } = useLoginRegisterModals(true);

  useEffect(() => {
    const routeType = sessionStorage.getItem("route_type");
    const eventContentId = sessionStorage.getItem("route_event_content_id");

    if (props.currentUser && routeType === "event" && eventContentId) {
      const selected = eventContents.find(ec => ec.id === Number(eventContentId)) || null;
      if (props.currentUser.isCompanyPerson) {
        alert(COMPANY_PERSON_MESSAGE);
      } else if (selected) {
        setState(prev => ({ ...prev, selectedEventContent: selected }));
        setModalMode('confirm');
        openEntryModal();
      }
    }

    sessionStorage.removeItem("route_type");
    sessionStorage.removeItem("route_event_content_id");
  }, []);

  const checkEntryEligibility = useCallback(() => {
    if (props.currentUser?.isCompany) {
      alert('求職者アカウントでログインされていません');
      return false;
    }

    if (!props.currentUser) {
      handleToggleRegisterModal();
      return false;
    }
    return true;
  }, [props.currentUser, handleToggleRegisterModal]);

  const handleApplyClick = useCallback((eventContentId: number) => {
    const selected = eventContents.find(ec => ec.id === eventContentId) || null;
    setState(prev => ({
      ...prev,
      selectedEventContent: selected,
    }));

    if (!checkEntryEligibility()) return;

    // キャンセルは社員でも行えるよう、checkEntryEligibilityではなく申し込み時だけ判定する
    if (props.currentUser?.isCompanyPerson) {
      alert(COMPANY_PERSON_MESSAGE);
      return;
    }

    setModalMode('confirm');
    openEntryModal();
  }, [checkEntryEligibility, eventContents, setState, openEntryModal, props.currentUser]);

  const handleModalClose = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedEventContent: null,
    }));
    closeEntryModal();
  }, [setState, closeEntryModal]);

  const handleApplyConfirm = useCallback(async () => {
    if (!state.selectedEventContent) return;
    setState(prev => ({ ...prev, isProcessing: true }));
    try {
      await applyEventContent(state.selectedEventContent.id, getCsrfToken());
      setModalMode('complete');
      setEventContents(prev =>
        prev.map(ec =>
          ec.id === state.selectedEventContent!.id
            ? { ...ec, isApplied: true, isCancelled: false }
            : ec
        )
      );
    } catch (error) {
      // 通信エラー等の英語メッセージは出さず、サーバーが返した文言のときだけそのまま表示する
      alert(error instanceof EntryEventContentApiError ? error.message : '申し込みに失敗しました');
    } finally {
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  }, [state.selectedEventContent, setState, setModalMode]);

  const handleCancelClick = useCallback(async (eventContentId: number) => {
    if (!checkEntryEligibility()) return;
    const selected = eventContents.find(ec => ec.id === eventContentId) || null;
    setState(prev => ({
      ...prev,
      selectedEventContent: selected,
    }));
    setModalMode('cancelled_confirm');
    openEntryModal();
  }, [checkEntryEligibility, eventContents, setState, openEntryModal]);

  const handleCancelConfirm = useCallback(async () => {
    if (!state.selectedEventContent) return;
    setState(prev => ({ ...prev, isProcessing: true }));
    try {
      await cancelEventContent(state.selectedEventContent.id, getCsrfToken());
      setModalMode('cancelled_complete');
      setEventContents(prev =>
        prev.map(ec =>
          ec.id === state.selectedEventContent!.id
            ? { ...ec, isApplied: false, isCancelled: true }
            : ec
        )
      );
    } catch (error) {
      alert('キャンセルに失敗しました');
    } finally {
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  }, [state.selectedEventContent, setState, setModalMode]);

  const presentationProps = {
    ...props,
    isPc,
    eventContents,
    state,
    onApplyClick: handleApplyClick,
    onApplyConfirm: handleApplyConfirm,
    onCancelClick: handleCancelClick,
    onCancelConfirm: handleCancelConfirm,
    onModalClose: handleModalClose,
    isEntryModalOpen: isEntryModalOpen,
    isLoginModalOpen: isLoginModalOpen,
    isRegisterModalOpen: isRegisterModalOpen,
    modalMode,
    redirectUrl,
    isRecruitmentOrEventOrTvOrCompanyPath: isRecruitmentOrEventOrTvOrCompanyPath,
    registerModalProps: {
      isModalOpen: isRegisterModalOpen,
      handleToggleRegisterModal,
      handleChangeModal,
      redirectUrl,
      isCarefully: false,
      isRequestPathRecruitmentOrEventOrTvOrFollow: isRecruitmentOrEventOrTvOrCompanyPath(),
      lineIcon: props.lineIcon,
    },
    loginModalProps: {
      isModalOpen: isLoginModalOpen,
      handleToggleLoginModal,
      handleChangeModal,
      redirectUrl,
      fromButton: 'event',
      eventContentId: state.selectedEventContent?.id,
      lineIcon: props.lineIcon,
    },
  };
  return <Presentation {...presentationProps} />;
};
